import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import i18n from '@/core/i18n/config';
import { useUpdatePreferences } from '../../hooks/useUpdatePreferences';

type SupportedLanguage = 'es' | 'en';

const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['es', 'en'];

interface LanguageOptionProps {
  value: SupportedLanguage;
  label: string;
  isActive: boolean;
  onSelect: (value: SupportedLanguage) => void;
}

function LanguageOption({ value, label, isActive, onSelect }: LanguageOptionProps) {
  return (
    <button
      type="button"
      data-testid={`settings-language-option-${value}`}
      onClick={() => onSelect(value)}
      className={cn(
        'flex items-center justify-between w-full px-4 py-3 rounded-lg border-2 transition-colors text-left',
        isActive
          ? 'border-primary bg-primary/5 text-primary font-medium'
          : 'border-border bg-muted/30 text-foreground hover:bg-muted',
      )}
    >
      <span className="text-sm">{label}</span>
      {isActive && <span className="w-2 h-2 rounded-full bg-primary shrink-0" aria-hidden="true" />}
    </button>
  );
}

interface LanguageSectionProps {
  showHeader?: boolean;
  serverLanguage?: string | null;
  onServerLanguageChange?: (lang: SupportedLanguage) => void;
}

export function LanguageSection({
  showHeader = true,
  serverLanguage,
  onServerLanguageChange,
}: LanguageSectionProps) {
  const { t } = useTranslation('settings');
  const updatePreferences = useUpdatePreferences();

  const currentLang = serverLanguage
    ? (serverLanguage as SupportedLanguage)
    : ((i18n.language as SupportedLanguage) ?? 'es');

  const handleLanguageChange = (lang: SupportedLanguage) => {
    if (onServerLanguageChange) {
      onServerLanguageChange(lang);
    } else {
      i18n.changeLanguage(lang);
      updatePreferences.mutate({ language: lang });
    }
  };

  return (
    <div className="space-y-4" data-testid="settings-language-section">
      {showHeader && (
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('language.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t('language.description')}</p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <LanguageOption
            key={lang}
            value={lang}
            label={t(`language.${lang}`)}
            isActive={currentLang === lang}
            onSelect={handleLanguageChange}
          />
        ))}
      </div>
    </div>
  );
}
